import { isBefore } from 'date-fns';

import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';

import { IKnmErknmFormResultDecision } from '../interfaces/IKnmErknmFormResultDecision';
import { isInvalidProductSalesFormDate, parseProductSalesFormDate } from './productSalesSuspensionDates';

export const PRODUCT_SALES_INVALID_DATE_ERROR = 'Укажите корректную дату в формате дд.мм.гггг.';
export const PRODUCT_SALES_VALID_TO_REQUIRED_ERROR =
  'Укажите дату окончания действия предписания или установите флажок «Бессрочно».';
export const PRODUCT_SALES_VALID_TO_BEFORE_FROM_ERROR = 'Дата окончания не может быть раньше даты выдачи предписания.';
export const PRODUCT_SALES_RESUMPTION_BEFORE_FROM_ERROR =
  'Дата возобновления не может быть раньше даты выдачи предписания.';

type ProductSalesFormDate = Parameters<typeof parseProductSalesFormDate>[0];
type ProductSalesDate = ReturnType<typeof parseProductSalesFormDate>;

interface IProductSalesResumptionErrors {
  resumptionDate?: string;
  decisionText?: string;
  documents?: string;
}

export const isProductSalesSuspensionLocked = (item: IKnmErknmFormResultDecision | undefined): boolean =>
  !!item?.prescriptionSigned || !!item?.signed;

const makeProductSalesDateFormatValidator = (errorMsg: string) => (value: ProductSalesFormDate) =>
  isInvalidProductSalesFormDate(value) ? errorMsg : undefined;

const makeProductSalesNoSoonerThanValidator = (errorMsg: string, getMinDate: () => ProductSalesDate | undefined) => (
  value: ProductSalesFormDate,
) => {
  const date = parseProductSalesFormDate(value);
  const minDate = getMinDate();

  return date && minDate && isBefore(date, minDate) ? errorMsg : undefined;
};

export const getProductSalesValidToValidate = (item: IKnmErknmFormResultDecision): string | undefined => {
  if (item.isIndefinite || isProductSalesSuspensionLocked(item)) {
    return undefined;
  }

  const dateDecision = parseProductSalesFormDate(item.dateDecision);

  return composeValidators(
    makeRequired(PRODUCT_SALES_VALID_TO_REQUIRED_ERROR),
    makeProductSalesDateFormatValidator(PRODUCT_SALES_INVALID_DATE_ERROR),
    makeProductSalesNoSoonerThanValidator(PRODUCT_SALES_VALID_TO_BEFORE_FROM_ERROR, () => dateDecision),
  )(item.validTo);
};

export const getProductSalesResumptionValidate = (
  item: IKnmErknmFormResultDecision,
): IProductSalesResumptionErrors | undefined => {
  const resumption = item.productSalesResumption;

  if (!isProductSalesSuspensionLocked(item) || item.resumptionSigned || !resumption) {
    return undefined;
  }

  const dateDecision = parseProductSalesFormDate(item.dateDecision);

  const errors: IProductSalesResumptionErrors = {
    resumptionDate: composeValidators(
      makeRequired(REQUIRED_ERROR),
      makeProductSalesDateFormatValidator(PRODUCT_SALES_INVALID_DATE_ERROR),
      makeProductSalesNoSoonerThanValidator(PRODUCT_SALES_RESUMPTION_BEFORE_FROM_ERROR, () => dateDecision),
    )(resumption.resumptionDate),
    decisionText: makeRequired(REQUIRED_ERROR)(resumption.decisionText),
    documents: resumption.documents?.some(document => !!document?.filePath) ? undefined : REQUIRED_ERROR,
  };

  return errors.resumptionDate || errors.decisionText || errors.documents ? errors : undefined;
};
